import React, { Fragment, useMemo } from 'react'
import PropTypes from 'prop-types'
import { domains } from 'config/env'
import NavigationHelper from 'layers/navigation/navigationHelper'
import { toKebabCase } from 'utils/stringHelpers'
import AdminSearchSelect from 'components/AdminSearchSelect'
import MultiColumnLinkPanel from './MultiColumnLinkPanel'
import {
  LinkImage,
  StyledOutlineButton,
  SupportLinksWrapper,
} from './component.styles'
import {
  LICENSE_MANAGEMENT,
  TRAINING_REPORT,
  USER_MANAGEMENT,
} from './constants'

const getLinkMenuColumns = link => {
  if (!link) return []

  return (
    Object.keys(link)
      // find the keys that start with 'linkMenu' and have an array value
      .filter(key => key.startsWith('linkMenu') && Array.isArray(link[key]))
      .map(key => {
        const items = link[key]
        // the first item with contentType.id === 'text' is the label for the column
        const label =
          items.find(item => item?.contentType?.id === 'text')?.content || ''
        // the items with contentType.id === 'linkButton' are the links for the column
        const links = items.filter(
          item => item?.contentType?.id === 'linkButton',
        )

        if (!links.length) return null

        return { id: key, label, links }
      })
      // remove any null values from the array
      .filter(Boolean)
  )
}

const AdminResourceLinkList = ({
  availableLinks,
  hasDwbAccess,
  navigationEvent,
  handleOpenModal,
}) => {
  const navigateHandler = (urlPath, openSameTab) => {
    const type = openSameTab
      ? NavigationHelper.types.PUSH
      : NavigationHelper.types.NEW_TAB_SAME_SITE

    if (
      urlPath.includes(LICENSE_MANAGEMENT) ||
      urlPath.includes(USER_MANAGEMENT) ||
      urlPath.includes(TRAINING_REPORT)
    ) {
      navigationEvent(
        `${domains.ADMIN_DOMAIN}${urlPath}`,
        NavigationHelper.types.OPEN,
      )
    } else {
      navigationEvent(urlPath, type)
    }
  }

  const renderSupportLinks = () => {
    let links
    if (availableLinks) {
      links = availableLinks.map(link => {
        const { buttonText, displayTitle, icon } = link || {}
        const { file, title } = icon || {}
        const { details, url: imgUrl } = file || {}
        const { image } = details || {}
        const { height, width } = image || {}

        const menuColumns = getLinkMenuColumns(link)
        const primaryMenu = menuColumns[0]

        const DropdownButtonIcon = dataTestId => (
          <LinkImage
            alt={title}
            dataTestId={dataTestId}
            height={height}
            key={dataTestId}
            src={imgUrl}
            width={width}
          />
        )

        const handleLinkAction = link => {
          if (link.modalId && handleOpenModal) {
            handleOpenModal(link.modalId)
          } else {
            navigateHandler(link.url, link.openSameTab)
          }
        }

        const useMultiColumnPanel = hasDwbAccess && menuColumns.length > 1

        const menuOptions = primaryMenu
          ? useMultiColumnPanel
            ? menuColumns.flatMap(column => column.links)
            : primaryMenu.links
          : []

        return (
          <Fragment key={title || buttonText}>
            {primaryMenu && (
              <AdminSearchSelect
                dataTestId={toKebabCase(displayTitle)}
                defaultLabel={displayTitle}
                dropdownButtonIcon={<DropdownButtonIcon />}
                keyProp="id"
                labelProp="buttonText"
                onChange={option => handleLinkAction(option)}
                options={menuOptions}
                renderPanel={
                  useMultiColumnPanel
                    ? ({ dataTestId, highlightedOptionId, onSelect }) => (
                        <MultiColumnLinkPanel
                          columns={menuColumns}
                          dataTestId={dataTestId}
                          highlightedOptionId={highlightedOptionId}
                          onSelect={onSelect}
                        />
                      )
                    : undefined
                }
                shouldHideSelectedIcon={true}
              />
            )}

            {!primaryMenu && (
              <StyledOutlineButton
                dataTestId={toKebabCase(buttonText)}
                onClick={() => handleLinkAction(link)}
              >
                <LinkImage
                  alt={title}
                  height={height}
                  src={imgUrl}
                  width={width}
                />
                {buttonText}
              </StyledOutlineButton>
            )}
          </Fragment>
        )
      })
    }
    return links
  }

  const supportLinks = useMemo(() => renderSupportLinks(), [
    (availableLinks || []).map(link => link.id).join(','),
    hasDwbAccess,
  ])

  return <SupportLinksWrapper>{supportLinks}</SupportLinksWrapper>
}

AdminResourceLinkList.propTypes = {
  availableLinks: PropTypes.array,
  handleOpenModal: PropTypes.func,
  hasDwbAccess: PropTypes.bool,
  navigationEvent: PropTypes.func,
}

export default AdminResourceLinkList
